// Public client invoice view at /i/:uuid (2026-06-19).
//
// The client opens this from the emailed link — no login. The share UUID
// is the only gate (mirrors PublicOrderPage at /o/:uuid). Display-only:
// payment is recorded by staff (manual) for now, so there's no pay button
// — just the invoice document + how-to-pay note + rep contact.

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Download } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { StatusPill } from "@/components/ui/StatusPill";
import { addressSnapshotLines } from "@/lib/address-snapshot";
import { DocumentPrintStyles } from "@/components/documents/PrintStyles";

interface AddressSnapshot {
  name?: string;
  company?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  [k: string]: unknown;
}

interface PublicInvoice {
  id: string;
  invoiceNumber: number;
  invoiceType: "deposit" | "final";
  status: string;
  invoiceDate: string;
  terms: string;
  /** YYYY-MM-DD; null = not set until everything is delivered. */
  dueDate?: string | null;
  subtotal: string;
  tax: string;
  total: string;
  depositApplied: string;
  balanceDue: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  /** Send stamp — null until the rep sends / link-publishes the invoice. */
  sentAt: string | null;
  customerPo: string | null;
  orderNumber: number;
  billingAddressSnapshot: AddressSnapshot | null;
  shippingAddressSnapshot: AddressSnapshot | null;
}

interface PublicLineVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  quantity: number;
  unitPrice: string | null;
}

interface PublicLine {
  id: string;
  lineNumber: number;
  serviceType: string;
  description: string | null;
  productSnapshot: {
    name?: string;
    imageUrls?: string[] | Record<string, string>;
    sku?: string;
  } | null;
  unitPrice: string;
  quantityTotal: number;
  variants: PublicLineVariant[];
}

interface PublicSentBy {
  name: string | null;
  email: string | null;
  phone: string | null;
}

interface PublicClient {
  id: string;
  name: string;
  projectName: string;
}

/** Server-authoritative payment block (2026-07-25). All amounts are integer
 *  cents computed from the ledger — the client never computes an amount. */
interface PublicPayment {
  /** What the client still owes, integer cents (deposit-netted, ledger-netted). */
  outstandingCents: number;
  /** Sum of succeeded payments already applied, integer cents. */
  paidCents: number;
  /** Card fee in cents (rides the charge only). */
  ccFeeCents: number;
  /** True when the client pays the card fee (it's in chargeCents). */
  feeIncluded: boolean;
  /** Card-surcharge percent for accurate disclosure copy (never hardcode 3%). */
  ccSurchargePct: number;
  /** What the card is charged: outstanding + fee. */
  chargeCents: number;
  /** Whether an online payment can start now. */
  payable: boolean;
  /** Why not, when payable === false. */
  reason:
    | "already_paid"
    | "voided"
    | "no_balance"
    | "card_disabled"
    | "not_sendable"
    | null;
}

interface PublicInvoiceResponse {
  invoice: PublicInvoice;
  payment: PublicPayment | null;
  lines: PublicLine[];
  client: PublicClient | null;
  sentBy: PublicSentBy | null;
  /** True when a staff JWT viewed the page — pre-send this is the ONLY way
   *  the page renders (anonymous traffic 404s until the invoice is sent). */
  staffPreview?: boolean;
}

const CURRENCY = "USD";

const COMPANY_INFO = {
  // Lowercase, the brand (Stephanie 2026-11-04).
  name: "other",
  line1: "3013 Yamato Rd. B12 Suite 301",
  cityLine: "Boca Raton, FL 33434",
  country: "United States",
};

function formatCurrency(n: number | string): string {
  const num = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(num)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso); // calendar day, not UTC midnight
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function lineImageUrl(line: PublicLine): string | null {
  const images = line.productSnapshot?.imageUrls;
  if (Array.isArray(images) && images.length > 0) {
    return typeof images[0] === "string" ? images[0] : null;
  }
  if (images && typeof images === "object") {
    const first = Object.values(images)[0];
    return typeof first === "string" ? first : null;
  }
  return null;
}

function variantLabel(v: PublicLineVariant): string {
  const parts: string[] = [];
  if (v.size) parts.push(`Size: ${v.size}`);
  if (v.color) parts.push(`Color: ${v.color}`);
  if (v.material) parts.push(v.material);
  if (parts.length === 0) return "Standard";
  return parts.join(" · ");
}

// Map the invoice status enum → brand StatusPill tone + a
// client-friendly label. Enum values: open / sent_to_client / exported /
// paid / voided (mirrors the staff InvoiceDetailPage convention).
function invoiceStatusTone(
  status: string,
): "active" | "dormant" | "closed" | "info" {
  switch (status) {
    case "paid":
      return "active";
    case "sent_to_client":
    case "exported":
      return "dormant";
    case "voided":
      return "closed";
    default:
      return "info";
  }
}

function invoiceStatusLabel(status: string): string {
  switch (status) {
    case "paid":
      return "Paid";
    case "sent_to_client":
    case "exported":
      return "Awaiting payment";
    case "voided":
      return "Voided";
    case "open":
      return "Open";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export function PublicInvoicePage(props: { previewUuid?: string } = {}) {
  // previewUuid: shown inside the Send window as a look-only preview of what
  // the client will see (Stephanie 2026-11-02: "there is also no preview").
  const params = useParams<{ uuid: string }>();
  const uuid = props.previewUuid ?? params.uuid ?? "";
  const embedded = props.previewUuid !== undefined;
  const [data, setData] = useState<PublicInvoiceResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [payMessage, setPayMessage] = useState<string | null>(null);
  // Wait for AuthProvider to settle so a logged-in staffer's JWT travels
  // with the fetch; anonymous clients simply send no token.
  const { loading: authLoading } = useAuth();

  // Pay this invoice — POST /i/:uuid/pay mints/reuses a Stripe Payment Link for
  // the OUTSTANDING balance (computed server-side; we send no amount) and we
  // redirect to Stripe's hosted page. Idempotent server-side against double-click.
  async function payInvoice(): Promise<void> {
    setPaying(true);
    setPayMessage(null);
    try {
      // skipAuth: the route is UUID-gated (public) — no client JWT is expected.
      // Matches PublicOrderPage's payRemainingBalance for one consistent story.
      const res = await api<{ url: string }>(`/i/${uuid}/pay`, {
        method: "POST",
        body: JSON.stringify({}),
        skipAuth: true,
      });
      if (res.url) {
        window.location.assign(res.url);
        return; // leaving the page; keep the button in its paying state
      }
      setPaying(false);
      setPayMessage("Couldn't start payment — please try again.");
    } catch (err) {
      setPaying(false);
      // 409 = the invoice changed under this open tab (e.g. it was just paid).
      // Friendly copy — never a raw HTTP status.
      if (err instanceof ApiError && err.status === 409) {
        setPayMessage(
          "This invoice can't be paid right now — please refresh, or reply to your rep's email.",
        );
      } else {
        setPayMessage(
          err instanceof Error ? err.message : "Couldn't start payment — try again.",
        );
      }
    }
  }

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    setLoadError(null);
    api<PublicInvoiceResponse>(`/i/${uuid}`, { skipAuthRefresh: true })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (!embedded) document.title = `Invoice #${res.invoice.invoiceNumber} · other`;
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          // Covers BOTH not-yet-sent and genuinely-gone — the API returns an
          // identical 404 for each on purpose (no existence leak), so the
          // copy has to speak to both without claiming either.
          setLoadError(
            "This invoice isn't available. It may not have been sent yet, or the link is no longer active.",
          );
        } else {
          setLoadError("Couldn't load this invoice. Try again later.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, authLoading]);

  const lines = useMemo(() => data?.lines ?? [], [data]);

  if (loadError) {
    return (
      <div className="min-h-screen bg-cream-50 px-6 py-24 text-center font-display">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Invoice not available
        </h1>
        <p className="mt-3 text-brown-400">{loadError}</p>
        <p className="mt-8 text-xs text-brown-300">
          If you think this is a mistake, please reply to the email you
          received with the link.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-cream-50 px-6 py-24 text-center text-brown-300">
        Loading invoice…
      </div>
    );
  }

  const { invoice, client, sentBy, payment } = data;
  // Every invoice is just an invoice (Stephanie 2026-10-27).
  const docLabel = "Invoice";
  const projectName = client?.projectName ?? "";
  const depositApplied = Number(invoice.depositApplied);
  const isPaid = invoice.status === "paid" || payment?.reason === "already_paid";
  // Outstanding comes from the server ledger when available (nets prior
  // payments); fall back to the invoice's balance_due for older payloads.
  const outstanding = payment
    ? payment.outstandingCents / 100
    : invoice.balanceDue != null
      ? Number(invoice.balanceDue)
      : Number(invoice.total);
  // Pre-send staff preview: anonymous traffic 404s until the invoice is sent,
  // so this state is only ever reachable by a rep. Suppress the whole payment
  // surface — the ledger says "payable" but the client hasn't been sent
  // anything yet, and a rep must not start a Stripe payment from a preview.
  const preSendPreview =
    data.staffPreview === true &&
    invoice.status === "open" &&
    !invoice.sentAt;
  const canPayOnline = payment?.payable === true && !preSendPreview;

  return (
    <div className="min-h-screen bg-cream-50 text-brown-500 font-sans print:min-h-0">
      <DocumentPrintStyles />
      {/* Invoice PDF = the PO PDF's fixes (Stephanie 2026-11-04: "make the same fixes
          as the PO"): no browser date / web address in the corners (page margin 0,
          our own band on every page), a size smaller, colours as on screen. */}
      <style>{`@media print {
        @page { margin: 0; }
        main { padding: 0 0.65in !important; zoom: 0.8; }
      }`}</style>
      <header className={`${embedded ? "relative" : "fixed top-0 left-0 right-0 z-50"} bg-cream-50/95 backdrop-blur-md border-b border-brown-100 print:hidden`}>
        <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 md:px-16 h-20">
          <a href="/" className="block" style={{ height: "44px" }} aria-label="Other">
            <img
              src="/brand/other-primary-lockup-black.svg"
              alt="other"
              style={{ height: "100%", width: "auto", display: "block" }}
            />
          </a>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brown-400 hover:text-rust-400 transition-colors"
          >
            <Download className="h-4 w-4" /> Download as PDF
          </button>
        </div>
      </header>

      <main className={`${embedded ? "pt-10" : "pt-32"} pb-20 print:pt-0 print:pb-0`}>
        {/* A blank band repeats at the top and bottom of EVERY printed page (a
            table's header/footer rows repeat per page). On screen: plain blocks. */}
        <table className="block w-full print:table">
          <thead className="hidden print:table-header-group"><tr><td><div style={{ height: "0.5in" }} /></td></tr></thead>
          <tfoot className="hidden print:table-footer-group"><tr><td><div style={{ height: "0.5in" }} /></td></tr></tfoot>
          <tbody className="block print:table-row-group"><tr className="block print:table-row"><td className="block print:table-cell">
        <div className="mx-auto max-w-[1280px] px-6 md:px-16 print:px-0">
          {/* Pre-send staff preview banner — mirrors PublicOrderPage's
              "Preview mode" banner. Only renders pre-send: once sent, a rep
              opening the client link sees exactly what the client sees. */}
          {preSendPreview && !embedded && (
            <div className="mb-8 rounded-md border border-peach-400 bg-peach-200 px-4 py-3 text-sm text-brown-500 print:hidden">
              <strong className="font-semibold">Preview mode.</strong> You're
              viewing this invoice as a rep — the client link won't activate
              until you click &quot;Send to client&quot; on the staff page.
            </div>
          )}

          {/* Document header */}
          <header className="mb-10 border-b border-brown-100 pb-7 print:mb-6 print:pb-5">
            {/* The page's top bar doesn't print, so the PDF shows who it's from here. */}
            <img src="/brand/other-primary-lockup-black.svg" alt="other" className="mb-5 hidden print:block" style={{ height: "36px", width: "auto" }} />
            <div className="mb-2.5 flex items-center gap-3">
              <p
                className="text-rust-400 uppercase"
                style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.14em" }}
              >
                {docLabel}
              </p>
              <StatusPill tone={invoiceStatusTone(invoice.status)}>
                {invoiceStatusLabel(invoice.status)}
              </StatusPill>
            </div>
            <h1
              className="font-display text-brown-500"
              style={{
                fontSize: "38px",
                lineHeight: 1.12,
                fontWeight: 700,
                letterSpacing: "-0.01em",
              }}
            >
              {client?.name
                ? `${client.name} ${docLabel} #${invoice.invoiceNumber}`
                : `${docLabel} #${invoice.invoiceNumber}`}
            </h1>
            {projectName && (
              <p
                className="mt-2 text-brown-400"
                style={{ fontSize: "18px", fontWeight: 600 }}
              >
                {projectName}
              </p>
            )}
          </header>

          {/* Addresses + metadata grid */}
          {/* On paper: Billing | Shipping side by side, then the invoice details in a row of 4 (as the PO). */}
          <section className="mb-10 grid gap-6 md:grid-cols-2 lg:grid-cols-5 print:mb-6 print:grid-cols-4 print:gap-5">
            <div className="print:col-span-2"><AddressBlock label="Billing address" address={invoice.billingAddressSnapshot} /></div>
            <div className="print:col-span-2"><AddressBlock label="Shipping address" address={invoice.shippingAddressSnapshot} /></div>
            <MetaRow label="Invoice #" value={String(invoice.invoiceNumber)} />
            <MetaRow label="Invoice date" value={formatDate(invoice.invoiceDate)} />
            <MetaRow label="Terms" value={invoice.terms.replace(/_/g, " ")} />
            {/* No "Due" date and no "Order #" (Stephanie 2026-11-02): the delivery
                date isn't known when the invoice goes out, and the client has no use
                for our order number. Terms (above) say when payment is due. */}
            {invoice.customerPo && (
              <MetaRow label="Customer PO" value={invoice.customerPo} />
            )}
          </section>

          {/* Line items */}
          <section className="mb-10 space-y-4">
            {lines.length === 0 ? (
              <p className="rounded-3xl border border-brown-100 bg-white px-6 py-10 text-center text-brown-300">
                No line items on this invoice.
              </p>
            ) : (
              lines.map((line) => <LineCard key={line.id} line={line} />)
            )}
          </section>

          {/* Totals — authoritative figures from the invoice record. */}
          <section className="mb-10 flex justify-end">
            <dl className="w-full max-w-sm rounded-3xl border border-brown-100 bg-white px-6 py-4">
              <Row label="Subtotal" value={formatCurrency(invoice.subtotal)} />
              {Number(invoice.tax) !== 0 && <Row label="Tax" value={formatCurrency(invoice.tax)} />}
              <Row label="Total" value={formatCurrency(invoice.total)} />
              {depositApplied > 0 && (
                <Row
                  label="Deposit applied"
                  value={`− ${formatCurrency(depositApplied)}`}
                />
              )}
              {payment && payment.paidCents > 0 && !isPaid && (
                <Row
                  label="Paid to date"
                  value={`− ${formatCurrency(payment.paidCents / 100)}`}
                />
              )}
              <div className="my-2 border-t border-brown-50" />
              {/* UI-POLISH: when paid, the standing "Payment received $X" banner
                  below owns the paid messaging — the totals row just states the
                  $0 balance plainly so paid-chrome isn't tripled (StatusPill +
                  banner + this). */}
              <Row
                label="Balance due"
                value={isPaid ? formatCurrency(0) : formatCurrency(outstanding)}
                emphasis
              />
            </dl>
          </section>

          {/* Standing "Paid" confirmation — mirrors the public Quote Paid
              banner so a client reopening a settled invoice always sees it. */}
          {isPaid && (
            <section className="mb-6 flex justify-end">
              <div className="w-full max-w-sm rounded-3xl border border-peach-400 bg-peach-100 px-6 py-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-brown-500 font-semibold" style={{ fontSize: "15px" }}>
                      Payment received
                    </p>
                    {invoice.paidAt && (
                      <p className="mt-0.5 text-brown-400" style={{ fontSize: "13px" }}>
                        Paid on {formatDate(invoice.paidAt)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1 text-white uppercase"
                      style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.08em" }}
                    >
                      <Check className="h-3.5 w-3.5" /> Paid
                    </span>
                    {payment && payment.paidCents > 0 && (
                      <p className="mt-1.5 text-brown-500 font-semibold" style={{ fontSize: "15px" }}>
                        {formatCurrency(payment.paidCents / 100)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Pay this invoice — online (Stripe) when the balance is payable by
              card; otherwise fall back to the reply-to-arrange note. Hidden
              entirely in a pre-send staff preview — "reply to the email you
              received" would be nonsense before any email exists. */}
          {!isPaid && invoice.status !== "voided" && !preSendPreview && (
            <section className="mb-6 rounded-3xl border border-brown-100 bg-white px-6 py-5 print:hidden">
              {canPayOnline && payment ? (
                <div className="text-center">
                  {/* Optional breakdown ABOVE the headline — the anchor number is
                      the ONE fee-inclusive charge, never a second figure. */}
                  {payment.feeIncluded && payment.ccFeeCents > 0 && (
                    <p className="text-brown-400" style={{ fontSize: "12px" }}>
                      Outstanding {formatCurrency(payment.outstandingCents / 100)} ·
                      Card fee {formatCurrency(payment.ccFeeCents / 100)}
                    </p>
                  )}
                  <p
                    className="text-brown-400 uppercase"
                    style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.08em" }}
                  >
                    Amount due
                  </p>
                  <p
                    className="mt-1 font-display text-brown-500"
                    style={{ fontSize: "30px", fontWeight: 700, letterSpacing: "-0.01em" }}
                  >
                    {formatCurrency(payment.chargeCents / 100)}
                  </p>
                  {payment.feeIncluded && payment.ccFeeCents > 0 && (
                    <p className="mt-1 text-brown-400" style={{ fontSize: "13px" }}>
                      Includes a {payment.ccSurchargePct}% card processing fee of{" "}
                      {formatCurrency(payment.ccFeeCents / 100)}.
                    </p>
                  )}
                  {payMessage && (
                    <p className="mt-3 text-sm text-rust-500">{payMessage}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => void payInvoice()}
                    disabled={paying}
                    className="mt-4 rounded-xl bg-ink px-10 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-ink-700 disabled:opacity-50"
                    style={{ letterSpacing: "0.08em" }}
                  >
                    {paying
                      ? "Starting payment…"
                      : `Pay ${formatCurrency(payment.chargeCents / 100)}`}
                  </button>
                  <p className="mt-3 text-xs text-brown-300">
                    Payment is handled on Stripe's secure page.
                  </p>
                </div>
              ) : (
                <>
                  <p
                    className="mb-2 text-brown-400 uppercase"
                    style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.08em" }}
                  >
                    How to pay
                  </p>
                  <p className="text-brown-400 text-sm" style={{ lineHeight: 1.65 }}>
                    Payment terms are{" "}
                    <strong className="text-brown-500">
                      {invoice.terms.replace(/_/g, " ")}
                    </strong>
                    . To arrange payment or ask anything about this invoice, just
                    reply to the email you received or reach out to your rep below.
                  </p>
                </>
              )}
            </section>
          )}

          {/* Questions? + Company info */}
          <section className="mb-6 rounded-3xl border border-brown-100 bg-white px-6 py-4">
            {sentBy && (sentBy.name || sentBy.email) && (
              <>
                <p
                  className="mb-2 text-brown-400 uppercase"
                  style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.08em" }}
                >
                  Questions about this invoice?
                </p>
                {sentBy.name && (
                  <p className="text-brown-500 font-semibold">{sentBy.name}</p>
                )}
                {sentBy.email && (
                  <p className="text-brown-400">
                    <a
                      href={`mailto:${sentBy.email}`}
                      className="text-ink hover:underline"
                    >
                      {sentBy.email}
                    </a>
                  </p>
                )}
                {sentBy.phone && (
                  <p className="text-brown-400 text-sm">{sentBy.phone}</p>
                )}
                <div className="my-4 border-t border-brown-50" />
              </>
            )}
            <p className="text-brown-500 font-semibold">{COMPANY_INFO.name}</p>
            <p className="text-brown-400 text-sm">{COMPANY_INFO.line1}</p>
            <p className="text-brown-400 text-sm">{COMPANY_INFO.cityLine}</p>
            {/* Our country isn't printed — we're in the US (Stephanie 2026-10-27). */}
          </section>
        </div>
          </td></tr></tbody>
        </table>
      </main>
    </div>
  );
}

function AddressBlock(props: { label: string; address: AddressSnapshot | null }) {
  const { label } = props;
  // Full address, whichever field names the snapshot uses.
  const address = addressSnapshotLines(props.address);
  return (
    <div className="md:col-span-1 lg:col-span-1">
      <p
        className="mb-1 text-brown-400 uppercase"
        style={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.08em" }}
      >
        {label}
      </p>
      {address ? (
        <div className="text-brown-500 text-sm leading-snug">
          {address.heading && <p className="font-semibold">{address.heading}</p>}
          {address.lines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      ) : (
        <p className="text-sm text-brown-300 italic">Not provided</p>
      )}
    </div>
  );
}

function MetaRow(props: { label: string; value: string }) {
  return (
    <div>
      <p
        className="mb-1 text-brown-400 uppercase"
        style={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.08em" }}
      >
        {props.label}
      </p>
      <p className="text-brown-500 text-sm font-semibold capitalize">
        {props.value}
      </p>
    </div>
  );
}

function Row(props: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <dt className={props.emphasis ? "font-semibold text-brown-500" : "text-brown-400"}>
        {props.label}
      </dt>
      <dd
        className={
          props.emphasis
            ? "font-bold text-rust-500 tabular-nums"
            : "text-brown-500 tabular-nums"
        }
      >
        {props.value}
      </dd>
    </div>
  );
}

function LineCard(props: { line: PublicLine }) {
  const { line } = props;
  const productName =
    line.productSnapshot?.name ||
    line.description ||
    (line.serviceType === "service" ? "Service" : "Item");
  const imageUrl = lineImageUrl(line);
  const unitPrice = Number(line.unitPrice);
  const rows =
    line.variants.length > 0
      ? line.variants.map((v) => ({
          id: v.id,
          label: variantLabel(v),
          qty: v.quantity,
          unit: Number(v.unitPrice ?? line.unitPrice),
        }))
      : [
          {
            id: `${line.id}-flat`,
            label: line.description ?? "Standard",
            qty: line.quantityTotal,
            unit: unitPrice,
          },
        ];
  const totalUnits = rows.reduce((acc, r) => acc + r.qty, 0);
  // The item's total is the rows above added up (Stephanie 2026-11-02: "why
  // does this say 18,000 dollars" — it multiplied the line's own price, which
  // can be out of date, by every unit, instead of each size/colour's price).
  // Shipping and services have no picture (Stephanie 2026-11-02).
  const isService = line.serviceType === "shipping" || line.serviceType === "service";
  const lineTotal =
    Math.round(rows.reduce((acc, r) => acc + (Number.isFinite(r.unit) ? r.unit * r.qty : 0), 0) * 100) / 100;

  return (
    <article className="rounded-3xl border border-brown-100 bg-white p-6 md:p-8 print:p-5">
      <h2
        className="font-display text-brown-500 mb-2"
        style={{ fontSize: "22px", lineHeight: 1.2, fontWeight: 600 }}
      >
        {productName}
      </h2>
      {line.description && line.description !== productName && (
        <p
          className="mb-4 whitespace-pre-wrap text-brown-400"
          style={{ fontSize: "14px", lineHeight: 1.6 }}
        >
          {line.description}
        </p>
      )}
      <div className={isService ? "grid gap-6" : "grid gap-6 md:grid-cols-[200px_1fr] print:grid-cols-[230px_1fr] print:gap-5"}>
        {!isService && (
        <div>
          <div className="aspect-square overflow-hidden rounded-2xl border border-brown-100 bg-white">
            {imageUrl ? (
              // The whole mock-up shows, never cropped (Stephanie 2026-11-02).
              <img
                src={imageUrl}
                alt={productName}
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-cream-100 text-[11px] uppercase tracking-wide text-brown-300">
                No image
              </div>
            )}
          </div>
        </div>
        )}

        <div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-brown-400">
                <th className="py-1.5">Item</th>
                <th className="py-1.5 text-right">Qty</th>
                <th className="py-1.5 text-right">Price</th>
                <th className="py-1.5 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="py-2 text-brown-500">{r.label}</td>
                  <td className="py-2 text-right tabular-nums text-brown-500">
                    {r.qty.toLocaleString()}
                  </td>
                  <td className="py-2 text-right tabular-nums text-brown-400">
                    {formatCurrency(r.unit)}
                  </td>
                  <td className="py-2 text-right tabular-nums text-brown-500">
                    {formatCurrency(r.unit * r.qty)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-brown-100 font-semibold">
                <td className="py-2 text-brown-500">Total units</td>
                <td className="py-2 text-right tabular-nums text-brown-500">
                  {totalUnits.toLocaleString()}
                </td>
                <td />
                <td className="py-2 text-right tabular-nums text-rust-500">
                  {formatCurrency(lineTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </article>
  );
}
